// @ts-check
import React from 'react';
import { merge } from 'lodash';

/** @typedef {Exclude<keyof import('./Base'), 'Page' | 'Body' | 'DynamicComponent'>} ThemedComponents */
/**
 * @typedef BottlebooksPDFTheme
 * @property {{
 *  page?: import('@react-pdf/types').Style;
 *  body?: import('@react-pdf/types').Style;
 *  colors?: Record<string, string>;
 *  components?:
 *    Partial<Record<ThemedComponents, Record<string, import('@react-pdf/types').Style>>> &
 *    { DynamicComponent?: Record<string, Record<string, Record<'component' | 'label' | 'value', import('@react-pdf/types').Style>>> };
 *  space?: Record<string, number | string>; // Space tokens. Currently only used in a few places, but we'll extend to all primitives.
 * }} theme
 * @property {"LEFT" | "RIGHT"} orientation
 */

const ThemeContext = React.createContext(
  /** @type {BottlebooksPDFTheme | undefined} */ ({
    theme: {},
    orientation: 'LEFT',
  })
);

export default function useTheme() {
  return React.useContext(ThemeContext);
}

/**
 * @param {object} props
 * @param {BottlebooksPDFTheme["theme"]} props.theme
 * @param {BottlebooksPDFTheme["orientation"]} [props.orientation="LEFT"]
 * @param {Record<string, import('@react-pdf/renderer').default.Styles>} [props.variants]
 * @param {string} [props.variant] - loads a variant theme
 * @param {object} [props.extend] - extends the specified theme/variant
 * @param {React.ReactNode} props.children
 * @returns
 */
export const ThemeProvider = ({
  theme,
  variants,
  variant,
  extend = {},
  orientation = 'LEFT',
  children,
}) => {
  const resolvedTheme = variant ? variants?.[variant] : theme;
  const extendedTheme = merge({}, resolvedTheme, extend);
  return (
    <ThemeContext.Provider value={{ theme: extendedTheme, orientation }}>
      {children}
    </ThemeContext.Provider>
  );
};
